import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { Toast } from "@smart/bridge-intake-components-dom";
import { buildMessageTemplate } from "@smart/bridge-templates-basic";
import { extractPrefixedTeamId } from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { Table, TableProps } from "@smart/itops-sb-design-system-dom";
import { extractId } from "@smart/itops-types-basic";
import { buildDisplayName, toSubdomain } from "@smart/itops-utils-basic";

import { Staff as StaffCell, FormTitle as FormTitleCell } from "./cells";
import { EmbedType } from "./embed-options";
import { LinkTableActions } from "./link-table-actions";
import { LinkItemType, Staff, SubmissionLink } from "./types";
import { sortSchema } from "../../home/hooks";

type LinksTableProps = {
  links: SubmissionLink[] | undefined;
  forms: { uri: string; values: { title: string } }[];
  staff: Staff[];
  user?: Staff;
  loading?: boolean;
  onEdit: (linkHash: SubmissionLink) => void;
  onDelete: (linkHash: SubmissionLink) => void;
  onCopy: ({ code, toast }: { code: string; toast: Toast }) => Promise<void>;
  onGenerateQRCode: (link: string) => void;
};

export const LinksTable = ({
  links,
  loading,
  forms,
  staff,
  user,
  onEdit,
  onDelete,
  onCopy,
  onGenerateQRCode,
}: LinksTableProps) => {
  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "formTitle", desc: false }],
    key: "web-enquiries-settings-table",
    schema: sortSchema,
    storage: "local",
  });

  const onCopyAction = (linkHash: SubmissionLink, type: EmbedType) => {
    const link = toSubdomain(undefined, `/create/${extractId(linkHash.uri)}`);

    onCopy({
      code:
        type === "url"
          ? link
          : buildMessageTemplate({
              link,
              format: "embed",
              category: "lead",
              matterType: { name: "" },
              form: { title: "" },
              embedOption: type,
            }).html,
      toast: {
        text: `${type === "url" ? "Link" : "Code"} copied and ready to be used`,
      },
    }).catch(console.error);
  };

  const onGenerateQRCodeAction = (linkHash: SubmissionLink) => {
    const link = toSubdomain(undefined, `/create/${extractId(linkHash.uri)}`);
    onGenerateQRCode(link);
  };

  const columns = useMemo(() => {
    const c = createColumnHelper<LinkItemType>();
    return [
      c.accessor("formTitle", {
        header: "Name",
        cell: FormTitleCell,
      }),
      c.accessor("staff", {
        header: "Staff",
        cell: StaffCell,
        meta: { className: "max-w-[230px]" },
      }),
      c.display({
        id: "actions",
        meta: { action: true },
        cell: LinkTableActions({
          onEdit,
          onDelete,
          onCopy: (linkHash, options) =>
            onCopyAction(linkHash, options?.copyType || "url"),
          onGenerateQRCode: onGenerateQRCodeAction,
        }),
      }),
    ];
  }, [staff]);

  const linksData =
    links
      ?.map((link) => {
        const formTitle =
          forms.find((form) => form.uri === link.formUri)?.values.title || "";
        const availableStaff = staff.find((s) => s.userId === user?.userId)
          ? staff
          : [...staff, user];
        const notifiedStaff = availableStaff.filter(
          (s) =>
            s &&
            (link.notifiedUserUris || [link.userUri])
              ?.map((uri) => extractPrefixedTeamId(uri, "sb").id)
              .includes(s.userId),
        );
        return {
          formTitle,
          staff: notifiedStaff
            .map((s) => buildDisplayName(s, s!.userId))
            .join(", "),
          link,
        };
      })
      .filter((l) => l.formTitle) || [];

  const sort: TableProps<LinkItemType>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  const select: TableProps<LinkItemType>["select"] = {
    state: {},
    onChange: (selected) => {
      const value = typeof selected === "function" ? selected({}) : selected;
      console.debug("selected rows: ", value);
    },
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <Table
        id="web-enquiries-settings-table"
        columns={columns}
        data={linksData}
        loadingRows={loading ? 3 : undefined}
        globalFilter={undefined}
        select={select}
        sort={sort}
        className="rounded-b-none"
        empty={`No web ${loadLocale().terms.enquiry.name} links created`}
      />
    </div>
  );
};
